// src/pages/healthcare/formsInTray/formStatusTransitions.ts

/**
 * ═══ `assigned_forms.status` — THE VOCABULARY AND THE MOVES BETWEEN ITS VALUES ═══
 * (ClickUp 86d3y4ear / S4)
 *
 * The five values were never the problem. What was missing was which MOVES between them are
 * legal: `Completed -> Pending` was accepted like any other edit, so a stale dropdown saved
 * alongside a notes correction silently undid a practitioner's sign-off and let the form be
 * submitted over. S1 gives healthcare's `FormStatus` enum the matrix below, S2 enforces it
 * with a 422, and S3 adds a deliberate, separately-audited reopen route for the two moves the
 * matrix withholds.
 *
 * This file is the FRONTEND MIRROR of that matrix. Its only job is that the tray never OFFERS
 * a move the backend will refuse — a practitioner should not meet a 422 they had no way to
 * predict.
 *
 * ⚠️ THIS IS UX, NOT THE GUARD. healthcare is the enforcement. The same PUT is reachable from
 * a console with the same token, so nothing here may be read as a security boundary.
 *
 * ⚠️ BEING A MIRROR, IT IS ONLY CORRECT WHILE IT AGREES WITH `FormStatus::canTransitionTo()`.
 * Change one and change the other in the same release; a drifted copy shows the practitioner
 * an option that 422s, or hides one that would have worked.
 *
 * The matrix:
 *
 *   Pending     -> In Progress, Completed, Cancelled
 *   In Progress -> Completed, Cancelled
 *   Completed   -> Reviewed, Cancelled     (-> Pending via the reopen route only)
 *   Reviewed    -> Completed, Cancelled    (-> Pending via the reopen route only)
 *   Cancelled   -> Pending
 *   X -> X      always allowed
 *
 * Two edges look surprising and are in it on purpose. `Reviewed -> Completed` un-reviews a
 * form, which undoes a practitioner's READ rather than a patient's submission — sending a
 * mis-click through the reopen override is exactly how an override stops being read as
 * deliberate. `Cancelled -> Pending` is the same class of mistake: cancelling is not a
 * submission, so uncancelling discards nothing. The only two moves that discard a submission
 * are `Completed -> Pending` and `Reviewed -> Pending`, and those are what reopen is for.
 *
 * ⚠️ `X -> X` IS LOAD-BEARING, not tidiness. `ReviewFormModal`'s `handleSave` sends `status`
 * on EVERY save, a notes-only one included, so the current value has to stay selectable or
 * notes edits on an In Progress / Reviewed / Cancelled row start failing.
 */

/**
 * The five canonical values. Every status dropdown in the tray — the page's filter and the
 * review modal's target picker — reads its labels from here, so a wording change lands in one
 * place and the filter can never disagree with the picker about what a status is called.
 */
export const STATUS_OPTIONS = [
	{ label: "Pending", value: "Pending" },
	{ label: "In Progress", value: "In Progress" },
	{ label: "Completed", value: "Completed" },
	{ label: "Reviewed", value: "Reviewed" },
	{ label: "Cancelled", value: "Cancelled" },
];

/**
 * Targets reachable from each status, EXCLUDING the always-legal self-transition — that one
 * is added by `allowedTransitions` rather than repeated five times, so it cannot be forgotten
 * on a row and quietly break notes saves for that status alone.
 */
const TRANSITIONS: Record<string, readonly string[]> = {
	Pending: ["In Progress", "Completed", "Cancelled"],
	"In Progress": ["Completed", "Cancelled"],
	Completed: ["Reviewed", "Cancelled"],
	Reviewed: ["Completed", "Cancelled"],
	Cancelled: ["Pending"],
};

/**
 * The statuses S3's reopen route accepts. Every other current status is a 422 there.
 *
 * ⚠️ This is the same pair as `utils/forms/formStatus`'s `DONE_FORM_STATUSES`, and it is
 * deliberately NOT derived from it. That module answers "is this assignment finished?"; this
 * one restates a route's accepted input. They coincide today because the moves that discard a
 * submission are exactly the ones from a finished state — couple them and a change to either
 * question silently answers the other.
 */
export const REOPENABLE_STATUSES: readonly string[] = ["Completed", "Reviewed"];

/** Whether S2 will accept this move. A self-transition is always true. */
export const canTransitionTo = (from: string, to: string): boolean =>
	from === to || (TRANSITIONS[from]?.includes(to) ?? false);

/**
 * The legal targets from `from`, itself included.
 *
 * An unrecognised `from` yields just itself. F6 constrains the column to the five values, so
 * this cannot happen against a real row — and answering with the full list "just in case"
 * would be the one behaviour that defeats the point of the file.
 */
export const allowedTransitions = (from: string): string[] => [from, ...(TRANSITIONS[from] ?? [])];

/**
 * The legal targets as dropdown options, FILTERED FROM `STATUS_OPTIONS` so the labels are not
 * restated here. Order follows `STATUS_OPTIONS`, so the picker's ordering never depends on
 * which status the row happens to be in.
 *
 * An unrecognised status yields an empty list. antd still renders the raw `value`, and
 * `handleSave` still sends it as a self-transition — so a notes-only save keeps working while
 * the (impossible) bad value stays visibly unchangeable rather than silently widening the
 * choices.
 */
export const allowedStatusOptions = (from: string): typeof STATUS_OPTIONS => {
	const allowed = allowedTransitions(from);
	return STATUS_OPTIONS.filter((option) => allowed.includes(option.value));
};

/** Whether S3's reopen route will accept a row in this status. */
export const canReopen = (status: string): boolean => REOPENABLE_STATUSES.includes(status);
